/**
 * Reports Index Page
 *
 * Navigation hub for all available reports.
 */

import { Link } from 'react-router-dom'
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Tag,
  FileText,
  Clock,
  AlertTriangle,
  Package,
  CreditCard,
  HandCoins,
  ChevronRight,
} from 'lucide-react'

import { useTranslation } from '@/hooks/useTranslation'
import { Card, CardContent } from '@/components/ui/card'

interface ReportItem {
  title: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  path: string
  color: string
}

export function ReportsIndexPage() {
  const { t } = useTranslation()

  const reports: ReportItem[] = [
    {
      title: t('reports.salesSummary'),
      description: t('reports.salesSummaryDesc'),
      icon: TrendingUp,
      path: '/reports/sales-summary',
      color: 'text-green-600 dark:text-green-400',
    },
    {
      title: t('reports.topSellingArticles'),
      description: t('reports.topSellingArticlesDesc'),
      icon: BarChart3,
      path: '/reports/top-articles',
      color: 'text-blue-600 dark:text-blue-400',
    },
    {
      title: t('reports.articleCosts'),
      description: t('reports.articleCostsDesc'),
      icon: DollarSign,
      path: '/reports/article-costs',
      color: 'text-emerald-600 dark:text-emerald-400',
    },
    {
      title: t('reports.articlePrices'),
      description: t('reports.articlePricesDesc'),
      icon: Tag,
      path: '/reports/article-prices',
      color: 'text-purple-600 dark:text-purple-400',
    },
    {
      title: t('reports.detailedSales'),
      description: t('reports.detailedSalesDesc'),
      icon: FileText,
      path: '/reports/detailed-sales',
      color: 'text-indigo-600 dark:text-indigo-400',
    },
    {
      title: t('reports.accountsReceivable'),
      description: t('reports.accountsReceivableDesc'),
      icon: Clock,
      path: '/reports/accounts-receivable',
      color: 'text-red-600 dark:text-red-400',
    },
    {
      title: t('reports.zeroStock'),
      description: t('reports.zeroStockDesc'),
      icon: AlertTriangle,
      path: '/reports/zero-stock',
      color: 'text-amber-600 dark:text-amber-400',
    },
    {
      title: t('reports.inventoryValue'),
      description: t('reports.inventoryValueDesc'),
      icon: Package,
      path: '/reports/inventory-value',
      color: 'text-cyan-600 dark:text-cyan-400',
    },
    {
      title: t('reports.accountsPayable'),
      description: t('reports.accountsPayableSubtitle'),
      icon: CreditCard,
      path: '/reports/accounts-payable',
      color: 'text-orange-600 dark:text-orange-400',
    },
    {
      title: t('reports.depositsHistory'),
      description: t('reports.depositsHistoryDesc'),
      icon: HandCoins,
      path: '/reports/deposits-history',
      color: 'text-emerald-600 dark:text-emerald-400',
    },
  ]

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-3xl font-bold">{t('reports.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('reports.selectReport')}</p>
      </div>

      {/* Reports Grid */}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 md:gap-4">
        {reports.map((report) => {
          const Icon = report.icon
          return (
            <Link
              key={report.path}
              to={report.path}
              className="group rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Card className="h-full group-hover:bg-muted/50 group-active:bg-muted transition-colors touch-manipulation">
                <CardContent className="p-4 md:p-5">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg bg-muted ${report.color}`}>
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="font-semibold text-base">{report.title}</h2>
                      <p className="text-sm text-muted-foreground line-clamp-2">{report.description}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" aria-hidden="true" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          )
        })}
      </div>
    </div>
  )
}
